/**
 * Trend chart grid for Cached Performance (CR-4751).
 *
 * Layout mirrors DefaultTab — one two-column grid, so a chart the Delivery
 * Metric Mode switches off (CR-8697) closes up rather than leaving a gap:
 *  - PPM + OTIF (each with target line)
 *  - Combined Financial (open orders + spend + arrears, dual Y-axis) + OTD
 *  - NCRs bar chart + Performance Summary card
 */
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ComposedChart, ReferenceLine,
} from 'recharts'
import { Info } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { ChartWrapper } from '../ChartWrapper'
import { useFormatters } from '../../../contexts/PreferencesContext'
import type {
  MetricType, DashboardSnapshot, TrendPoint,
} from '../../../lib/api/dashboard-snapshots'
import type { DeliveryMetricMode } from '../../../lib/api'
import type { PerformanceTargets } from '../../../hooks/usePerformanceHistory'
import { DAILY_HIDDEN_NOTE, NA_LABEL, asNumber, mergeSeries, toChartSeries, withTargets } from './helpers'
import { sharedTarget } from '../periodTarget'
import { showsOtd, showsOtif } from '../deliveryMetricMode'

function ChartEmpty({ reason }: { reason: string }) {
  return (
    <div className="h-[200px] flex flex-col items-center justify-center text-muted-foreground gap-2">
      <Info className="w-6 h-6" />
      <p className="text-sm">{reason}</p>
    </div>
  )
}

interface CachedTrendChartsProps {
  trends: Record<MetricType, TrendPoint[]>
  latestByMetric: Map<MetricType, DashboardSnapshot>
  targets: PerformanceTargets
  period: 'daily' | 'weekly' | 'monthly' | 'quarterly'
  /** Period metrics with nothing to measure in the latest capture (CR-4751). */
  naMetrics?: Set<MetricType>
  /** CR-8697: the customer's Delivery Metric Mode — hides the OTIF / OTD charts it switches off. */
  deliveryMetricMode?: DeliveryMetricMode
}

export function CachedTrendCharts({
  trends, latestByMetric, targets, period, naMetrics = new Set<MetricType>(), deliveryMetricMode,
}: CachedTrendChartsProps) {
  const { preferences, formatCurrency, formatCompactCurrency, formatNumber } = useFormatters()
  const daily = period === 'daily'
  const showOTIF = showsOtif(deliveryMetricMode)
  const showOTD = showsOtd(deliveryMetricMode)
  // CR-4751: the user's number format, and "—" / "N/A" for no figure (a
  // missing snapshot read as 0 — "Total GRNs 0" before any capture).
  const summary = (metric: MetricType): string => {
    if (naMetrics.has(metric)) return NA_LABEL
    const snap = latestByMetric.get(metric)
    return snap ? formatNumber(Math.round(asNumber(snap.metric_value))) : '—'
  }
  // CR-6266: each point carries the target in force on its snapshot date, so
  // the dashed target line steps where a target range starts.
  const series = (points: TrendPoint[], kpi: 'ppm' | 'otif' | 'otd') =>
    withTargets(toChartSeries(points, period), points, targets.ranges?.[kpi], targets[kpi])
  const ppmSeries = series(trends.ppm ?? [], 'ppm')
  const otifSeries = series(trends.otif_percentage ?? [], 'otif')
  const otdSeries = series(trends.otd_percentage ?? [], 'otd')
  // One labelled full-width line while the target is shared; a step where a range changes it.
  const targetLine = (points: Array<{ target?: number }>, color: string, unit: string) => {
    const flat = sharedTarget(points)
    return flat !== null
      ? <ReferenceLine y={flat} stroke={color} strokeDasharray="4 4" strokeWidth={2}
          label={{ value: `Target ${flat}${unit}`, position: 'right', fill: color, fontSize: 10 }} />
      : <Line type="step" dataKey="target" stroke={color} strokeDasharray="4 4" strokeWidth={2}
          dot={false} activeDot={false} tooltipType="none" name="Target" />
  }
  // CR-4751: why a delivery chart is empty, or null when it has points.
  const deliveryEmpty = (points: unknown[], noun: string): string | null => {
    if (daily) return DAILY_HIDDEN_NOTE
    return points.length === 0 ? `No ${noun} snapshots yet.` : null
  }
  const otifEmpty = deliveryEmpty(otifSeries, 'OTIF')
  const otdEmpty = deliveryEmpty(otdSeries, 'OTD')
  // ChartWrapper prints its own generic "No data for the selected period" for
  // an EMPTY data array, over the chart's children — so a chart that explains
  // its emptiness passes no data at all (CR-4751: the Daily note never showed).
  const exportable = <T,>(series: T[], empty: boolean): T[] | undefined => (empty ? undefined : series)
  const ncrSeries = toChartSeries(trends.ncr_count ?? [], period)

  // Combined Financial chart (open orders + spend + arrears), dual Y-axis
  const financialMerged = mergeSeries([
    { key: 'open_orders', points: trends.open_order_value ?? [] },
    { key: 'spend', points: trends.monthly_spend ?? [] },
    { key: 'arrears', points: trends.arrears_value ?? [] },
  ], period)

  return (
    <div className="grid gap-4 grid-cols-2">
      {/* PPM + OTIF */}
      <ChartWrapper
        title="PPM" data={exportable(ppmSeries, ppmSeries.length === 0)} exportFilename="cached-ppm-trend"
        legend={[
          { color: '#3b82f6', label: 'PPM', dataKey: 'value' },
          ...(targets.ppm !== null ? [{ color: '#ef4444', label: 'Target', dataKey: 'target' }] : []),
        ]}>
        {ppmSeries.length === 0 ? <ChartEmpty reason="No PPM snapshots yet." /> : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={ppmSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="PPM" />
              {targets.ppm !== null && targetLine(ppmSeries, '#ef4444', '')}
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartWrapper>

      {showOTIF && (
        <ChartWrapper
          title="OTIF %" data={exportable(otifSeries, otifEmpty !== null)} exportFilename="cached-otif-trend"
          legend={[
            { color: '#1d4ed8', label: 'OTIF %', dataKey: 'value' },
            ...(targets.otif !== null ? [{ color: '#dc2626', label: 'Target', dataKey: 'target' }] : []),
          ]}>
          {otifEmpty ? <ChartEmpty reason={otifEmpty} /> : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={otifSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(v: number) => `${v.toFixed(1)}%`} />
                <Line type="monotone" dataKey="value" stroke="#1d4ed8" strokeWidth={3} dot={{ r: 5, fill: '#1d4ed8', stroke: '#fff', strokeWidth: 2 }} name="OTIF %" />
                {targets.otif !== null && targetLine(otifSeries, '#dc2626', '%')}
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartWrapper>
      )}

      {/* Combined Financial (dual Y-axis) + OTD */}
      <ChartWrapper
        title={`Financial Trends (${preferences.currency})`} data={financialMerged} exportFilename="cached-financial-trend"
        legend={[
          { color: '#2563eb', label: 'Open Orders', dataKey: 'open_orders' },
          { color: '#059669', label: 'Monthly Spend', dataKey: 'spend' },
          { color: '#dc2626', label: 'Arrears', dataKey: 'arrears' },
        ]}>
        {financialMerged.length === 0 ? <ChartEmpty reason="No financial snapshots yet." /> : (
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={financialMerged}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v: number) => formatCompactCurrency(v)} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v: number) => formatCompactCurrency(v)} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(v: number) => formatCurrency(v)} />
              <Line yAxisId="left" type="monotone" dataKey="open_orders" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} name="Open Orders" />
              <Line yAxisId="left" type="monotone" dataKey="spend" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="Monthly Spend" />
              <Line yAxisId="right" type="monotone" dataKey="arrears" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} name="Arrears" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </ChartWrapper>

      {showOTD && (
        <ChartWrapper
          title="OTD %" data={exportable(otdSeries, otdEmpty !== null)} exportFilename="cached-otd-trend"
          legend={[
            { color: '#1d4ed8', label: 'OTD %', dataKey: 'value' },
            ...(targets.otd !== null ? [{ color: '#dc2626', label: 'Target', dataKey: 'target' }] : []),
          ]}>
          {otdEmpty ? <ChartEmpty reason={otdEmpty} /> : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={otdSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(v: number) => `${v.toFixed(1)}%`} />
                <Line type="monotone" dataKey="value" stroke="#1d4ed8" strokeWidth={3} dot={{ r: 5, fill: '#1d4ed8', stroke: '#fff', strokeWidth: 2 }} name="OTD %" />
                {targets.otd !== null && targetLine(otdSeries, '#dc2626', '%')}
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartWrapper>
      )}

      {/* NCRs + Performance Summary */}
      <ChartWrapper
        title="NCRs Raised" data={ncrSeries} exportFilename="cached-ncrs-trend"
        legend={[{ color: '#f59e0b', label: 'NCRs', dataKey: 'value' }]}>
        {ncrSeries.length === 0 ? <ChartEmpty reason="No NCR snapshots yet." /> : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={ncrSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="value" fill="#f59e0b" name="NCRs" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartWrapper>

      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Performance Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 h-[200px] items-center">
            <div className="text-center p-4 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Total GRNs</p>
              <p className="text-2xl font-bold">{summary('total_grns')}</p>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Open NCRs</p>
              <p className="text-2xl font-bold">{summary('ncr_open_count')}</p>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Active Suppliers</p>
              {daily
                ? <p className="text-[11px] text-muted-foreground">{DAILY_HIDDEN_NOTE}</p>
                : <p className="text-2xl font-bold">{summary('active_supplier_count')}</p>}
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Arrears Lines</p>
              <p className="text-2xl font-bold">{summary('arrears_count')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
